import { getFontColorForBackgroundContrast, rgbaToHex } from "QuorumGrassroots/styled-components/helperFunctions"
import styled from "styled-components"

type RGB = {
    r: number
    g: number
    b: number
}

const hexOrRgbToRgb = (color: string): RGB => {
    const normalized = color.trim()

    if (normalized.startsWith("rgb")) {
        const matches = normalized.match(/\d+(\.\d+)?/g)
        if (matches && matches.length >= 3) {
            const [r, g, b] = matches
            return {
                r: Number(r),
                g: Number(g),
                b: Number(b),
            }
        }
    }

    let hex = normalized.replace("#", "")
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((char) => `${char}${char}`)
            .join("")
    }

    const num = parseInt(hex, 16)

    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    }
}

const rgbToHex = ({ r, g, b }: RGB): string =>
    `#${[r, g, b]
        .map((value) => {
            const hex = value.toString(16)
            return hex.length === 1 ? `0${hex}` : hex
        })
        .join("")}`

const rgbToRgba = ({ r, g, b }: RGB, alpha = 1): string =>
    `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`

const getLuminance = ({ r, g, b }: RGB): number => {
    const channels = [r, g, b].map((value) => {
        const channel = value / 255
        return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
    })

    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

const lighten = ({ r, g, b }: RGB, amount: number): RGB => ({
    r: Math.round(r + (255 - r) * amount),
    g: Math.round(g + (255 - g) * amount),
    b: Math.round(b + (255 - b) * amount),
})

const darken = ({ r, g, b }: RGB, amount: number): RGB => ({
    r: Math.round(r * (1 - amount)),
    g: Math.round(g * (1 - amount)),
    b: Math.round(b * (1 - amount)),
})

export const generateSmartPalette = (baseColor: string): string[] => {
    const rgb = hexOrRgbToRgb(baseColor)
    const luminance = getLuminance(rgb)
    const isLight = luminance > 0.5

    const palette: string[] = []

    for (let i = 1; i <= 9; i += 1) {
        const amount = i * 0.05
        const variantRgb = isLight ? darken(rgb, amount) : lighten(rgb, amount)
        palette.push(rgbToHex(variantRgb))
    }

    palette.unshift(baseColor)

    if (isLight) palette.reverse()

    const alphaPalette: string[] = []

    for (let i = 1; i <= 9; i += 1) {
        const alpha = 1 - i * 0.05
        alphaPalette.push(rgbToRgba(rgb, alpha))
    }

    // If the base color is light, the palette contains darker variations.
    // If the base color is dark, the palette contains lighter variations.
    return alphaPalette
}

export const organizationPrimaryColor = window.organization_design.primary_color
export const organizationPalette = generateSmartPalette(organizationPrimaryColor)

export const ContainerWithColor = styled.div`
    --organization-primary-color-0: ${organizationPalette[9]};
    --organization-primary-color-1: ${organizationPalette[8]};
    --organization-primary-color-2: ${organizationPalette[7]};
    --organization-primary-color-3: ${organizationPalette[6]};
    --organization-primary-color-4: ${organizationPalette[5]};
    --organization-primary-color-5: ${organizationPalette[4]};
    --organization-primary-color-6: ${organizationPalette[3]};
    --organization-primary-color-7: ${organizationPalette[2]};
    --organization-primary-color-8: ${organizationPalette[1]};
    --organization-primary-color-9: ${organizationPalette[0]};

    --contrasted-organization-primary-color-0: ${getFontColorForBackgroundContrast(
        organizationPalette[9] ?? organizationPalette[8],
    )};
    --contrasted-organization-primary-color-1: ${getFontColorForBackgroundContrast(organizationPalette[8])};
    --contrasted-organization-primary-color-2: ${getFontColorForBackgroundContrast(organizationPalette[7])};
    --contrasted-organization-primary-color-3: ${getFontColorForBackgroundContrast(organizationPalette[6])};
    --contrasted-organization-primary-color-4: ${getFontColorForBackgroundContrast(organizationPalette[5])};
    --contrasted-organization-primary-color-5: ${getFontColorForBackgroundContrast(organizationPalette[4])};
    --contrasted-organization-primary-color-6: ${getFontColorForBackgroundContrast(organizationPalette[3])};
    --contrasted-organization-primary-color-7: ${getFontColorForBackgroundContrast(organizationPalette[2])};
    --contrasted-organization-primary-color-8: ${getFontColorForBackgroundContrast(organizationPalette[1])};
    --contrasted-organization-primary-color-9: ${getFontColorForBackgroundContrast(organizationPalette[0])};
`
